import { Search as SearchIcon } from '@mui/icons-material';
import {
  Autocomplete,
  InputAdornment,
  Paper,
  Popper as MuiPopper,
  TextField,
} from '@mui/material';
import { debounce } from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Configure, useHits, useSearchBox } from 'react-instantsearch';

import ContactFormDialog from '@/components/ContactFormDialog/ContactFormDialog';
import ProductAutocompleteField from '@/components/ProductAutocompleteField/ProductAutocompleteField';
import { Popper } from '@/components/ProductAutocompleteField/ProductAutocompleteField.style';
import { ProductHitContent } from '@/components/RentalRequestFlow/AutocompleteRow';
import DropdownEmptyState from '@/components/RentalRequestFlow/DropdownEmptyState';
import { searchLabelProps } from '@/components/RentalRequestFlow/SearchInput';
import SearchEngine, {
  isUsingAlgolia,
} from '@/components/SearchEngine/SearchEngine';
import {
  trackHeroContactFormOpen,
  trackHeroSearchSelectFreetext,
  trackHeroSearchSelectProduct,
  trackHeroSearchZeroResults,
} from '@/helpers/heroSearchEvents';
import useFormatMessage from '@/hooks/useFormatMessage';

const HITS_PER_PAGE = 20;
const SURFACE = 'basket_dialog';

export const mapHitToRentalObject = hit => ({
  name: hit.name,
  productReferenceId: hit.id ?? null,
  salesforceId: hit.salesforce_id ?? null,
  productGroupName: hit.product_group_name ?? null,
  productGroupId: hit.product_group_id ?? null,
  automatedOffer: Boolean(hit.send_automated_offer),
  sendAutomatedOffer: Boolean(hit.send_automated_offer),
  minLeadTime: hit.min_lead_time ?? null,
  rentalPriceBasis: hit.rental_price_basis ?? null,
  rentalPrice: hit.rental_price ?? null,
  invoiceBasis: hit.rental_price_basis ?? null,
});

const ProductSearchFieldInner = ({
  value,
  onChange,
  onBlur,
  inputRef,
  helperText,
  insideDialog,
  ...restProps
}) => {
  const formatMessage = useFormatMessage();

  const queryHook = useMemo(
    () => debounce((query, search) => search(query), 200),
    []
  );
  const { refine } = useSearchBox({ queryHook });
  const { items, results } = useHits();

  const rootRef = useRef(null);
  const [focused, setFocused] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);

  // The index returns only hits, so compare the query that produced them.
  const noResults =
    Boolean(value.trim()) &&
    results?._state?.query === value &&
    items.length === 0;

  useEffect(() => {
    if (noResults) trackHeroSearchZeroResults(SURFACE, { query: value });
  }, [noResults, value]);

  useEffect(() => {
    refine(value);
  }, [value, refine]);

  return (
    <>
      <Configure hitsPerPage={HITS_PER_PAGE} />
      <Autocomplete
        ref={rootRef}
        id="productSearchField"
        data-test-subject="product-search-input"
        options={items}
        value={value || null}
        // freeSolo never clears the text for a null value, so a form reset needs this.
        inputValue={value}
        onInputChange={(event, inputValue, reason) => {
          if (reason === 'input' || reason === 'clear') {
            onChange({ name: inputValue });
          }
        }}
        onChange={(event, hit, reason) => {
          if (reason === 'selectOption' && hit) {
            trackHeroSearchSelectProduct(SURFACE, {
              query: value,
              productSlug: hit.slug ?? null,
              position: items.indexOf(hit),
              section: 'products',
            });
            onChange(mapHitToRentalObject(hit));
          }
        }}
        getOptionLabel={option =>
          typeof option === 'string' ? option : option.name
        }
        renderOption={(props, option) => (
          <li {...props} key={option.objectID}>
            <ProductHitContent
              hit={option}
              primary={option.name}
              secondary={option.category_name}
            />
          </li>
        )}
        ListboxProps={{
          sx: {
            '& .MuiAutocomplete-option': { p: 0 },
            '& .MuiAutocomplete-option.Mui-focused': {
              backgroundColor: 'action.hover',
            },
          },
        }}
        PopperComponent={Popper}
        slotProps={{ popper: { insideDialog } }}
        renderInput={params => (
          <TextField
            {...params}
            {...restProps}
            label={formatMessage('dr_form_machine')}
            InputLabelProps={searchLabelProps(focused || Boolean(value))}
            helperText={helperText}
            onFocus={() => setFocused(true)}
            onBlur={event => {
              setFocused(false);
              onBlur(event);
            }}
            inputRef={inputRef}
            autoComplete="off"
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
          />
        )}
        noOptionsText={formatMessage('rental_object_suggestion_empty_label')}
        filterOptions={x => x}
        openOnFocus
        blurOnSelect
        fullWidth
        freeSolo
        clearText={formatMessage('rental_request_flow_clear_aria')}
        sx={{ '& .MuiAutocomplete-clearIndicator': { visibility: 'visible' } }}
      />
      <MuiPopper
        open={focused && noResults}
        anchorEl={rootRef.current}
        placement="bottom-start"
        style={{ width: rootRef.current?.clientWidth, zIndex: 1300 }}>
        {/* Keeps focus in the input, so the popper survives a click on its buttons. */}
        <Paper
          elevation={4}
          sx={{ mt: 1 }}
          onMouseDown={e => e.preventDefault()}>
          <DropdownEmptyState
            query={value}
            variant="zero_results"
            onRequestFreeText={query => {
              trackHeroSearchSelectFreetext(SURFACE, {
                query,
                source: 'zero_results',
              });
              document.activeElement?.blur();
            }}
            onOpenContact={() => {
              trackHeroContactFormOpen(SURFACE, { source: 'zero_results' });
              setContactOpen(true);
            }}
          />
        </Paper>
      </MuiPopper>
      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
      />
    </>
  );
};

const propTypes = {
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  value: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  inputRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  insideDialog: PropTypes.bool,
};

const defaultProps = {
  value: '',
  required: false,
  error: false,
  helperText: '',
  inputRef: null,
  insideDialog: false,
};

ProductSearchFieldInner.propTypes = propTypes;
ProductSearchFieldInner.defaultProps = defaultProps;

const ProductSearchField = props =>
  isUsingAlgolia ? (
    <SearchEngine>
      <ProductSearchFieldInner {...props} />
    </SearchEngine>
  ) : (
    <ProductAutocompleteField {...props} only="rentable" />
  );

ProductSearchField.propTypes = propTypes;
ProductSearchField.defaultProps = defaultProps;

export default ProductSearchField;
